/**
 * Associate Container Modal
 * Modal for manually associating an email to a specific container ID
 */
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { ComboBox } from "@/components/base/select/combobox";
import { SelectItem } from "@/components/base/select/select-item";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { X, Link03 } from "@untitledui/icons";
import { useContainerTable } from "@/hooks/api/use-containers-recap";

interface AssociateContainerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (containerId: string) => void;
    isLoading?: boolean;
}

export const AssociateContainerModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
}: AssociateContainerModalProps) => {
    const { t } = useTranslation(["pages:activity"]);
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);

    // Fetch all containers (no pagination, we need all for the combobox)
    // Note: Backend has a max limit of 200, so we use that
    const { data: containersData, isLoading: isLoadingContainers } = useContainerTable({
        startDate: null,
        endDate: null,
        skip: 0,
        limit: 200, // backend max is 200
        enabled: isOpen // only fetch when modal is open
    });

    // Map containers to ComboBox items
    const containerItems = useMemo(() => {
        if (!containersData?.containers) return [];

        return containersData.containers
            .filter(c => c.container_number) // Only containers with a number
            .map(container => ({
                id: container.container_id,
                label: container.container_number || 'Unknown',
                supportingText: container.booking_number ? `Booking: ${container.booking_number}` : undefined,
                description: `${container.origin_port_name || 'N/A'} → ${container.destination_port_name || 'N/A'}`,
            }));
    }, [containersData]);

    const handleConfirm = () => {
        if (selectedContainerId) {
            onConfirm(selectedContainerId);
        }
    };

    const handleClose = () => {
        setSelectedContainerId(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <Modal className="max-w-md">
                <Dialog>
                    <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-primary">
                                {t("emailActions.associateContainer.title", { defaultValue: "Associa Email a Container" })}
                            </h2>
                            <Button
                                onClick={handleClose}
                                color="tertiary"
                                size="sm"
                                iconLeading={X}
                                aria-label="Close"
                            />
                        </div>

                        {/* Icon */}
                        <div className="mb-4 flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-secondary">
                                <Link03 className="h-6 w-6 text-brand-primary" />
                            </div>
                        </div>

                        {/* Description */}
                        <p className="mb-4 text-center text-sm text-tertiary">
                            {t("emailActions.associateContainer.description", { defaultValue: "Seleziona il container a cui vuoi associare questa email. Il sistema processerà automaticamente il contenuto." })}
                        </p>

                        {/* ComboBox for Container Selection */}
                        <div className="mb-6">
                            <ComboBox
                                label={t("emailActions.associateContainer.inputLabel", { defaultValue: "Container Number" })}
                                placeholder={t("emailActions.associateContainer.inputPlaceholder", { defaultValue: "Cerca e seleziona un container..." })}
                                items={containerItems}
                                defaultItems={containerItems}
                                selectedKey={selectedContainerId}
                                onSelectionChange={(key) => setSelectedContainerId(key as string)}
                                isDisabled={isLoading || isLoadingContainers}
                                allowsEmptyCollection={false}
                            >
                                {(item) => (
                                    <SelectItem id={item.id} textValue={item.label}>
                                        <div className="flex flex-col">
                                            <span className="font-medium">{item.label}</span>
                                            {item.supportingText && (
                                                <span className="text-xs text-tertiary">{item.supportingText}</span>
                                            )}
                                            {item.description && (
                                                <span className="text-xs text-tertiary">{item.description}</span>
                                            )}
                                        </div>
                                    </SelectItem>
                                )}
                            </ComboBox>
                            {isLoadingContainers && (
                                <p className="mt-1 text-xs text-tertiary">
                                    {t("emailActions.associateContainer.loadingContainers", { defaultValue: "Caricamento container..." })}
                                </p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={handleClose}
                                isDisabled={isLoading}
                                color="secondary"
                                size="sm"
                            >
                                {t("emailActions.associateContainer.cancel", { defaultValue: "Annulla" })}
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                isDisabled={isLoading || !selectedContainerId || isLoadingContainers}
                                isLoading={isLoading}
                                color="primary"
                                size="sm"
                                iconLeading={!isLoading ? Link03 : undefined}
                            >
                                {isLoading ? t("emailActions.associateContainer.associating", { defaultValue: "Associando..." }) : t("emailActions.associateContainer.confirm", { defaultValue: "Associa" })}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
